import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  Chip
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, PlayArrow as PlayIcon } from '@mui/icons-material';
import AITaskForm from '../../../components/admin/ai-orchestrator/AITaskForm';
import { useSnackbar } from 'notistack';
import aiOrchestratorService from '../../../services/aiOrchestratorService';
import AdminLayout from '../../../components/admin/AdminLayout';

const AIOrchestratorTasks = () => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [tasks, setTasks] = useState([]);
  const [agents, setAgents] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchTasks = async () => {
    try {
      const response = await aiOrchestratorService.getTasks({ orchestratorId: id });
      setTasks(response.data || []);
      setLoading(false);
    } catch (error) {
      enqueueSnackbar('Failed to fetch tasks', { variant: 'error' });
      setLoading(false);
    }
  };

  const fetchAgents = async () => {
    try {
      const response = await aiOrchestratorService.getOrchestrator(id);
      setAgents(response.data.agents || []);
    } catch (error) {
      enqueueSnackbar('Failed to fetch agents', { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchAgents();
  }, [id]);

  const handleSubmit = async (formData) => {
    try {
      if (selectedTask) {
        await aiOrchestratorService.updateTask(id, selectedTask._id, formData);
        enqueueSnackbar('Task updated successfully', { variant: 'success' });
      } else {
        await aiOrchestratorService.scheduleTask({ ...formData, orchestratorId: id });
        enqueueSnackbar('Task created successfully', { variant: 'success' });
      }
      setOpenDialog(false);
      setSelectedTask(null);
      fetchTasks();
    } catch (error) {
      enqueueSnackbar(error.message || 'Operation failed', { variant: 'error' });
    }
  };

  const handleDelete = async (taskId) => {
    if (window.confirm('Are you sure you want to delete this task?')) {
      try {
        await aiOrchestratorService.deleteTask(id, taskId);
        enqueueSnackbar('Task deleted successfully', { variant: 'success' });
        fetchTasks();
      } catch (error) {
        enqueueSnackbar('Failed to delete task', { variant: 'error' });
      }
    }
  };

  const handleExecute = async (taskId) => {
    try {
      await aiOrchestratorService.executeTask({ orchestratorId: id, taskId });
      enqueueSnackbar('Task execution started', { variant: 'success' });
      fetchTasks();
    } catch (error) {
      enqueueSnackbar('Failed to execute task', { variant: 'error' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'info';
      case 'scheduled':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">AI Tasks</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedTask(null);
            setOpenDialog(true);
          }}
        >
          Add Task
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Agent</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Scheduled For</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task) => (
              <TableRow key={task._id}>
                <TableCell>
                  {agents.find(a => a._id === task.agentId)?.name || task.agentId}
                </TableCell>
                <TableCell>{task.taskType}</TableCell>
                <TableCell>
                  <Chip
                    label={task.status}
                    color={getStatusColor(task.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>{task.priority}</TableCell>
                <TableCell>
                  {new Date(task.scheduledFor).toLocaleString()}
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => {
                      setSelectedTask(task);
                      setOpenDialog(true);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(task._id)}>
                    <DeleteIcon />
                  </IconButton>
                  {task.status === 'scheduled' && (
                    <IconButton onClick={() => handleExecute(task._id)}>
                      <PlayIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={openDialog}
        onClose={() => {
          setOpenDialog(false);
          setSelectedTask(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <AITaskForm
          initialData={selectedTask}
          onSubmit={handleSubmit}
          isEditMode={!!selectedTask}
          agents={agents}
        />
      </Dialog>
    </Box>
    </AdminLayout>
  );
};

export default AIOrchestratorTasks; 